import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, MapPin, Plane, Clock, Users, Phone, Calendar, Save,
  ArrowLeft, PoundSterling, Link as LinkIcon, MessageCircle,
  ShieldCheck, User, Lock, LogOut,
} from "lucide-react";
import { fetchBookings, updateBooking, formatGBP, adminLogin, getAdminToken, clearAdminToken } from "../lib/booking";

const STATUS = ["pending", "confirmed", "completed", "cancelled"];
const PAYMENT = ["unpaid", "link_sent", "paid", "refunded"];

const STATUS_STYLE = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  confirmed: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const SELECT =
  "rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none";

const BookingCard = ({ b, onSaved }) => {
  const [status, setStatus] = useState(b.status || "pending");
  const [paymentStatus, setPaymentStatus] = useState(b.payment_status || "unpaid");
  const [paymentUrl, setPaymentUrl] = useState(b.payment_url || "");
  const [saving, setSaving] = useState(false);

  const dirty =
    status !== (b.status || "pending") ||
    paymentStatus !== (b.payment_status || "unpaid") ||
    paymentUrl !== (b.payment_url || "");

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateBooking(b.ref, {
        status,
        payment_status: paymentStatus,
        payment_url: paymentUrl || null,
      });
      onSaved(updated);
      toast.success(`Booking ${b.ref} updated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update booking.");
    } finally {
      setSaving(false);
    }
  };

  const phoneDigits = (b.customer?.phone || "").replace(/\D/g, "");

  const copyPayLink = async () => {
    const url = `${window.location.origin}/pay/${b.ref}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied");
    } catch {
      toast.message(url);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={`admin-booking-${b.ref}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="font-mono text-sm tracking-wider text-gold">{b.ref}</span>
          <p className="mt-0.5 text-[0.7rem] uppercase tracking-widest text-white/40">
            Booked {fmtDateTime(b.created_at)}
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-[0.65rem] font-medium uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
          {status}
        </span>
      </div>

      <div className="mt-4 space-y-1.5 text-sm text-white/80">
        <p className="flex items-center gap-2 font-heading text-base text-white">
          {b.service_type === "hourly" ? <Clock size={15} className="text-gold" /> : <Plane size={15} className="text-gold" />}
          {b.service_label}
        </p>
        {b.pickup && (
          <p className="flex items-start gap-2 text-white/70">
            <MapPin size={14} className="mt-0.5 shrink-0 text-white/40" />
            {b.pickup.description}
            {b.dropoff ? <span className="text-white/40"> → {b.dropoff.description}</span> : null}
          </p>
        )}
        <p className="flex items-center gap-2 text-white/60">
          <Calendar size={14} className="text-white/40" /> {fmtDateTime(b.datetime)}
          {b.hours ? <span className="ml-1 text-white/40">· {b.hours}h</span> : null}
        </p>
        {b.flight_number && <p className="text-white/60">Flight: {b.flight_number}</p>}
        {b.return_trip && b.return_datetime && (
          <p className="text-white/60">Return: {fmtDateTime(b.return_datetime)}</p>
        )}
      </div>

      <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-3">
        <div>
          <p className="text-sm text-white/80">
            <Users size={13} className="mr-1 inline text-gold" />
            {b.vehicle_class?.name} <span className="text-white/45">({b.vehicle_class?.model})</span>
          </p>
          <p className="mt-1 text-xs text-white/60">
            {b.customer?.name || "—"}
            {b.customer?.phone && (
              <span className="ml-2 inline-flex items-center gap-2">
                <a href={`tel:${b.customer.phone}`} className="inline-flex items-center gap-1 text-white/70 hover:text-gold">
                  <Phone size={12} /> {b.customer.phone}
                </a>
                {phoneDigits && (
                  <a
                    href={`https://wa.me/${phoneDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#25D366] hover:opacity-80"
                  >
                    <MessageCircle size={12} />
                  </a>
                )}
              </span>
            )}
          </p>
        </div>
        <span className="font-heading text-2xl text-gold">{formatGBP(b.total)}</span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-[0.65rem] uppercase tracking-wider text-white/40">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={SELECT} data-testid={`admin-status-${b.ref}`}>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[0.65rem] uppercase tracking-wider text-white/40">
          Payment
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={SELECT} data-testid={`admin-payment-${b.ref}`}>
            {PAYMENT.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-2">
        <label className="flex flex-col gap-1 text-[0.65rem] uppercase tracking-wider text-white/40">
          Payment link (sent to customer later)
          <div className="relative">
            <LinkIcon size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={paymentUrl}
              onChange={(e) => setPaymentUrl(e.target.value)}
              placeholder="https://pay.example.com/…"
              data-testid={`admin-payurl-${b.ref}`}
              className="w-full rounded-lg border border-white/15 bg-white/5 py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-gold/60 focus:outline-none"
            />
          </div>
        </label>
        <button
          type="button"
          onClick={copyPayLink}
          data-testid={`admin-copy-paylink-${b.ref}`}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gold/40 bg-gold/10 px-3 py-1.5 text-[0.7rem] font-medium uppercase tracking-wider text-gold transition-colors hover:bg-gold/20"
        >
          <LinkIcon size={13} /> Copy Stripe payment link
        </button>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={!dirty || saving}
        data-testid={`admin-save-${b.ref}`}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-gold py-2.5 text-xs font-medium uppercase tracking-widest text-ink transition-colors hover:bg-gold-light disabled:opacity-40"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save changes
      </button>
    </div>
  );
};

const AdminLogin = ({ onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setBusy(true);
    try {
      await adminLogin(username.trim(), password);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed. Check your details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-7" data-testid="admin-login-form">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold">
            <ShieldCheck size={20} />
          </div>
          <h1 className="font-heading text-2xl font-light text-white">Admin <span className="italic text-gold">access</span></h1>
          <p className="mt-1 text-xs text-white/45">Upper Class Chauffeurs — bookings</p>
        </div>
        <label className="mb-3 block">
          <span className="mb-1 block text-[0.7rem] uppercase tracking-widest text-white/45">Username</span>
          <div className="relative">
            <User size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={username} onChange={(e) => setUsername(e.target.value)} data-testid="admin-username" autoComplete="username"
              className="w-full rounded-lg border border-white/15 bg-white/5 py-2.5 pl-9 pr-3 text-sm text-white focus:border-gold/60 focus:outline-none" />
          </div>
        </label>
        <label className="mb-5 block">
          <span className="mb-1 block text-[0.7rem] uppercase tracking-widest text-white/45">Password</span>
          <div className="relative">
            <Lock size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" autoComplete="current-password"
              className="w-full rounded-lg border border-white/15 bg-white/5 py-2.5 pl-9 pr-3 text-sm text-white focus:border-gold/60 focus:outline-none" />
          </div>
        </label>
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-gold py-3 text-sm font-medium uppercase tracking-widest text-ink transition-colors hover:bg-gold-light disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
};

export default function BookingsDashboard() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      setBookings(await fetchBookings());
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
      } else {
        toast.error("Could not load bookings.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
    setBookings([]);
  };

  const onSaved = (updated) =>
    setBookings((prev) => prev.map((b) => (b.ref === updated.ref ? updated : b)));

  const revenue = useMemo(
    () => bookings.filter((b) => b.status !== "cancelled").reduce((s, b) => s + (b.total || 0), 0),
    [bookings]
  );

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <a href="/" className="mb-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/50 hover:text-gold">
              <ArrowLeft size={14} /> Back to site
            </a>
            <h1 className="font-heading text-3xl font-light text-white sm:text-4xl">
              Bookings <span className="italic text-gold">dashboard</span>
            </h1>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white transition-colors hover:border-gold/50"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/70 transition-colors hover:border-rose-400/50 hover:text-rose-300"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:max-w-md">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[0.65rem] uppercase tracking-widest text-white/40">Total bookings</p>
            <p className="mt-1 font-heading text-2xl text-white" data-testid="admin-count">{bookings.length}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[0.65rem] uppercase tracking-widest text-white/40">Pipeline value</p>
            <p className="mt-1 flex items-center font-heading text-2xl text-gold">
              <PoundSterling size={18} className="mr-0.5" />{revenue.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {["all", ...STATUS].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                filter === s ? "border-gold bg-gold text-ink" : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-20 flex items-center justify-center text-white/50">
            <Loader2 size={22} className="mr-2 animate-spin" /> Loading bookings…
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-20 text-center text-white/40" data-testid="admin-empty">
            No bookings{filter !== "all" ? ` with status "${filter}"` : " yet"}.
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {filtered.map((b) => (
              <BookingCard key={b.ref} b={b} onSaved={onSaved} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
